import { Helmet } from 'react-helmet-async';
import { useParams, Navigate } from 'react-router-dom';
import { blogPosts } from './data/blogPost';
import { Container, Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { images } from 'config/images';
import { useState, useEffect } from 'react';

const baseUrl = 'https://www.e-smartwash.co.nz';

export const BlogPost = () => {
   const { slug } = useParams<{ slug: string }>();
   const post = blogPosts.find(p => p.slug === slug);
   const theme = useTheme();
   const matches = useMediaQuery(theme.breakpoints.up("md"));
   const [content, setContent] = useState<string>('');
   const [loading, setLoading] = useState(true);
   const [notFound, setNotFound] = useState(false);

   useEffect(() => {
      if (!slug || !post) {
         setNotFound(true);
         return;
      }

      const loadContent = async () => {
         try {
            setLoading(true);
            // Dynamic import of content file based on slug
            const contentModule = await import(`./content/${slug}.tsx`);
            setContent(contentModule.content);
         } catch (error) {
            console.error('Failed to load blog content:', error);
            setNotFound(true);
         } finally {
            setLoading(false);
         }
      };

      loadContent();
   }, [slug, post]);

   // If post metadata not found or content failed to load
   if (notFound || (!post && !loading)) {
      return <Navigate to="/blog" replace />;
   }

   if (loading) {
      return (
         <Container>
            <Box py={6} textAlign="center">
               <Typography>Loading...</Typography>
            </Box>
         </Container>
      );
   }

   const meta = {
      title: post!.meta.title || 'Blog Post - E-SmartWash',
      description: post!.meta.description || 'Read our latest blog post.',
      breadcrumb: [
         { position: 1, name: 'Home', item: `${baseUrl}/` },
         { position: 2, name: 'Blog', item: `${baseUrl}/blog/` },
         { position: 3, name: post!.meta.title, item: `${baseUrl}/blog/${slug}/` },
      ],
   };

   const getBreadcrumbSchema = () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": meta.breadcrumb.map(b => ({
         "@type": "ListItem",
         position: b.position,
         name: b.name,
         item: b.item,
      })),
   });

   return (
      <>
         <Helmet>
            <title>{meta.title} - E-SmartWash</title>
            <meta name="description" content={meta.description} />
            <link rel="canonical" href={`${baseUrl}/blog/${slug}/`} />
            <script type="application/ld+json">
               {JSON.stringify(getBreadcrumbSchema())}
            </script>
         </Helmet>

         <main className="blog-post">
            <Box position="relative" width="100%" height={matches ? "50vh" : "40vh"} overflow="hidden">
               <Box
                  component="img"
                  src={images.home.hero}
                  alt="Hero banner"
                  loading="eager"
                  {...{ fetchpriority: "high" } as any}
                  sx={{
                     position: "absolute",
                     top: 0,
                     left: 0,
                     width: "100%",
                     height: "100%",
                     objectFit: "cover",
                     zIndex: 0,
                     filter: 'brightness(0.6)',
                  }}
               />

               <Box
                  position="relative"
                  zIndex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  height="100%"
                  color={theme.palette.common.white}
                  px={theme.spacing(4)}
               >
                  <Typography
                     variant="h1"
                     fontWeight={800}
                     textAlign="center"
                     textTransform={'capitalize'}
                     width={matches ? '60%' : '100%'}
                     sx={{
                        textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                        padding: theme.spacing(2),
                     }}
                  >
                     {post!.meta.title}
                  </Typography>
               </Box>
            </Box>

            <Box component={"section"} py={6}>
               <Container>
                  <div
                     className="blog-content"
                     dangerouslySetInnerHTML={{ __html: content }}
                  />
               </Container>
            </Box>


         </main>
      </>
   );
};
